import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { WorkoutExercise } from '@strength/domain-workout';
import { formatISODate } from '@strength/utils';
import { MdClose } from '@teamworksdev/icons';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionExercises } from '../../../types';
import { getLocale } from '../../../../../utils/i18n';

const MAX_NOTE_LENGTH = 1000;

export const TabletExerciseNoteModal = ({
  athlete,
  workout,
  exercise,
  open,
  onClose,
  onSave,
}: {
  athlete: AthletePreview;
  workout: Pick<SectionExercises, 'workoutId' | 'workoutName'> | undefined;
  exercise: WorkoutExercise | undefined;
  open: boolean;
  onClose: () => void;
  onSave: (
    athleteId: string,
    workoutId: string,
    exerciseId: string,
    note: string,
    workoutExerciseId: string,
  ) => void;
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const currentDateTime = formatISODate(DateTime.now().toISO(), getLocale(), {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (!workout || !exercise) {
    // this should never happen, but we need to handle it
    return null;
  }

  return (
    <Dialog
      key={'note-modal'}
      onClose={onClose}
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '500px',
        },
      }}
    >
      <DialogTitle className="flex flex-row justify-between items-center">
        {athleteDisplayName(athlete)}
        <div className="flex flex-row items-center gap-1">
          <Typography variant="body2" color="gray">
            {currentDateTime}
          </Typography>
          <IconButton onClick={onClose} aria-label={t('close')}>
            <MdClose size={24} aria-hidden="true" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0 }}
      >
        <Typography variant="body2Medium">{exercise.exerciseName}</Typography>
        <Typography variant="body2Medium" color="gray">
          {workout.workoutName}
        </Typography>

        <TextField
          multiline
          label={t('note')}
          minRows={5}
          maxRows={5}
          value={note}
          inputProps={{ maxLength: MAX_NOTE_LENGTH }}
          onChange={(e) => setNote(e.target.value)}
          helperText={`${note.length}/${MAX_NOTE_LENGTH}`}
          error={note.length > MAX_NOTE_LENGTH}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={note.length === 0 || note.length > MAX_NOTE_LENGTH}
          onClick={() => {
            onSave(
              athlete.id,
              workout.workoutId,
              exercise.libraryExerciseId,
              note,
              exercise.workoutExerciseId,
            );
            onClose();
          }}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
